/*
 * ALOHA QUEEN SUPERNOVA - Admin Panel
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import RealisticGalaxyBackground from '../components/RealisticGalaxyBackground';
import SystemStatusDashboard from '../components/SystemStatusDashboard';

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    // Check if user is admin (LIFETIME_ENTERPRISE tier or specific admin flag)
    if (user.subscription_tier !== 'LIFETIME_ENTERPRISE' && !user.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchAdminData();
  }, [user, navigate]);

  const fetchAdminData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch platform stats
      const statsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

      // Fetch users
      const usersResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData.users || []);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateUserTier = async (userId, newTier) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/tier`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ tier: newTier })
      });

      if (response.ok) {
        fetchAdminData(); // Refresh data
      }
    } catch (error) {
      console.error('Error updating user tier:', error);
    }
  };

  if (!user || loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900">
      <RealisticGalaxyBackground />
      
      <div className="relative z-10 container mx-auto px-4 pt-24 pb-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">👑 Admin Control Panel</h1>
          <p className="text-gray-300">Aloha Queen Supernova - System Administration</p>
        </div>

        {/* System Status Dashboard */}
        <div className="mb-8">
          <SystemStatusDashboard token={localStorage.getItem('token')} />
        </div>

        {/* Platform Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-xl p-6 border border-blue-500/30">
              <div className="text-blue-300 text-sm mb-2">Total Users</div>
              <div className="text-white text-3xl font-bold">{stats.total_users || 0}</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-xl p-6 border border-purple-500/30">
              <div className="text-purple-300 text-sm mb-2">Active Subscriptions</div>
              <div className="text-white text-3xl font-bold">{stats.active_subscriptions || 0}</div>
            </div>
            <div className="bg-gradient-to-br from-pink-500/20 to-pink-600/20 backdrop-blur-sm rounded-xl p-6 border border-pink-500/30">
              <div className="text-pink-300 text-sm mb-2">Avatar Sessions</div>
              <div className="text-white text-3xl font-bold">{stats.total_sessions || 0}</div>
            </div>
            <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 backdrop-blur-sm rounded-xl p-6 border border-green-500/30">
              <div className="text-green-300 text-sm mb-2">Revenue (MTD)</div>
              <div className="text-white text-3xl font-bold">${stats.monthly_revenue || '0'}</div>
            </div>
          </div>
        )}

        {/* Users Management */}
        <div className="bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/30 shadow-2xl">
          <h2 className="text-2xl font-bold text-white mb-6">User Management</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-gray-300 border-b border-gray-700">
                  <th className="pb-4 pr-4">User</th>
                  <th className="pb-4 pr-4">Email</th>
                  <th className="pb-4 pr-4">Tier</th>
                  <th className="pb-4 pr-4">Joined</th>
                  <th className="pb-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-gray-700/50 text-gray-300">
                    <td className="py-4 pr-4">{u.full_name}</td>
                    <td className="py-4 pr-4">{u.email}</td>
                    <td className="py-4 pr-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        u.subscription_tier === 'LIFETIME_ENTERPRISE' ? 'bg-yellow-500/20 text-yellow-300' :
                        u.subscription_tier === 'ENTERPRISE' ? 'bg-purple-500/20 text-purple-300' :
                        u.subscription_tier === 'PRO' ? 'bg-blue-500/20 text-blue-300' :
                        u.subscription_tier === 'STARTER' ? 'bg-green-500/20 text-green-300' :
                        'bg-gray-500/20 text-gray-300'
                      }`}>
                        {u.subscription_tier}
                      </span>
                    </td>
                    <td className="py-4 pr-4">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="py-4">
                      <select
                        value={u.subscription_tier}
                        onChange={(e) => updateUserTier(u.id, e.target.value)}
                        className="bg-gray-700 text-white px-3 py-1 rounded text-sm border border-gray-600"
                      >
                        <option value="FREE">FREE</option>
                        <option value="STARTER">STARTER</option>
                        <option value="PRO">PRO</option>
                        <option value="ENTERPRISE">ENTERPRISE</option>
                        <option value="LIFETIME_ENTERPRISE">LIFETIME</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* System Settings */}
        <div className="mt-8 bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/30 shadow-2xl">
          <h2 className="text-2xl font-bold text-white mb-6">System Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Payment Settings</h3>
              <div className="space-y-2 text-gray-300 text-sm">
                <p>✓ Stripe Integration Active</p>
                <p>✓ Webhook Configured</p>
                <p>✓ Live Mode Enabled</p>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">AI Agent Settings</h3>
              <div className="space-y-2 text-gray-300 text-sm">
                <p>✓ Emergent LLM Active</p>
                <p>✓ Voice Synthesis Enabled</p>
                <p>✓ 3D Avatars Available</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;